import type { LatLng, LeafletMouseEvent, Map, MapOptions } from "leaflet";
import { lazy, Suspense, useState } from "react";
import type { FC, ReactNode } from "react";
import { MapContext } from "./leaflet-context";
import type {
  MapEventsProps,
  LeafletLocationSearchProps,
  OSMRes,
} from "~/types/ui";
import { cn } from "~/../lib/utils";

const LazyLeafletMap = lazy(() => import("./leaflet-base-map"));
const LazyMapEvents = lazy<FC<MapEventsProps>>(
  () => import("./leaflet-mapevents-hooks"),
);
const LazyLocationSearch = lazy<FC<LeafletLocationSearchProps>>(
  () => import("./leaflet-location-search"),
);

const LazyMapConsumer = lazy<FC<{ setMap: (m: Map) => void }>>(
  () => import("./leaflet-map-consumer"),
);

const LazyMarkerClusterGroup = lazy<FC<any>>(
  () => import("react-leaflet-markercluster"),
);
const LazyMarker = lazy(() => import("./leaflet-marker"));

export const LeafletMap: FC<
  {
    center: [number, number] | LatLng;
    markers?: [number, number][];
    children?: ReactNode; // Renders outside the Leaflet MapContainer.`
    legend?: ReactNode;
    clusterGroup?: ReactNode;
    mapController?: ReactNode; // Renders inside the Leaflet MapContainer. Required for content using useMap() OR useMapEvents().
    className?: string;
    onClick?: (map: Map, e: LeafletMouseEvent) => void;
    onLocationSearchSelect?: (map: Map, res: OSMRes) => void;
    /** A configured tile source; unset or unknown is the default source. */
    tileSourceId?: string;
    /** Off for a map that isn't of the globe (a floor plan on `CRS.Simple`). */
    tiles?: boolean;
  } & MapOptions
> = ({
  center,
  tileSourceId,
  tiles,
  markers,
  onClick,
  children,
  onLocationSearchSelect,
  legend,
  clusterGroup,
  mapController,
  className,
  ...options
}) => {
  const [map, setMap] = useState<Map | null>(null);

  return (
    <Suspense fallback={<div className="h-[400px]" />}>
      <MapContext.Provider value={{ map, setMap }}>
        {children}
        {/* Capped to the map so a tall legend scrolls instead of spilling past it. */}
        {legend && (
          <div className="absolute top-2 right-2 z-10 max-h-[calc(100%-1rem)] overflow-y-auto">
            {legend}
          </div>
        )}

        {onLocationSearchSelect && (
          <>
            <LazyLocationSearch onSelect={onLocationSearchSelect} />
            <div className="mb-2" />
          </>
        )}

        <LazyLeafletMap
          center={center}
          zoom={13}
          tileSourceId={tileSourceId}
          tiles={tiles}
          className={cn("h-[400px] w-full relative z-0", className)}
          {...options}
        >
          <Suspense fallback={<></>}>
            <LazyMapEvents onClick={onClick} />

            {mapController}

            {clusterGroup ?? (
              <LazyMarkerClusterGroup>
                {markers?.map((position: [number, number], index: number) => (
                  <LazyMarker key={index} position={position} />
                ))}
              </LazyMarkerClusterGroup>
            )}

            <LazyMapConsumer setMap={setMap} />
          </Suspense>
        </LazyLeafletMap>
      </MapContext.Provider>
    </Suspense>
  );
};
